import { useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

// "Filter By" pill for the public pages. Keeps the Figma pill look, but the
// whole pill (text and chevron) is one button that opens a real option
// list, since a native <select> can't be opened by clicking the chevron.
// Figma filter pill (488:46116): 3px faint border, 10px/4px padding. The
// Overview market-prices pill (997:57143) is the same with a 12px gap.
const PILL_BASE = 'border-[3px] border-[rgba(187,187,187,0.1)] px-[10px] py-1 leading-[21px]';
const PILL_STYLES = {
  default: `gap-2 ${PILL_BASE}`,
  compact: `gap-3 ${PILL_BASE}`,
};

// Closes an open popover on outside click or Escape — shared with FilterPanel
export function useDismiss(containerRef, isOpen, close) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) close();
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [containerRef, isOpen, close]);
}

// The pill trigger itself — shared with FilterPanel so every filter matches
export function FilterPill({ label, isOpen, onClick, ariaLabel, haspopup = 'listbox', variant = 'default' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup={haspopup}
      aria-expanded={isOpen}
      aria-label={ariaLabel}
      className={`flex cursor-pointer items-center rounded-full bg-white text-[12px] font-medium text-[#18181b] ${PILL_STYLES[variant]}`}
    >
      <span className="whitespace-nowrap pr-1">{label}</span>
      <FiChevronDown className={`h-3 w-3 shrink-0 text-[#18181b] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
}

export default function PublicFilterDropdown({ value, options, onChange, ariaLabel, align = 'left', variant = 'default' }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedRef = useRef(null);

  const close = useRef(() => setIsOpen(false)).current;
  useDismiss(containerRef, isOpen, close);

  // Bring the current selection into view in long lists (e.g. 37 states)
  useEffect(() => {
    if (isOpen) selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen]);

  const select = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <FilterPill
        label={value}
        isOpen={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        ariaLabel={ariaLabel}
        variant={variant}
      />

      {isOpen && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className={`absolute z-30 mt-2 max-h-72 min-w-full w-max max-w-[16rem] overflow-y-auto rounded-2xl border border-gray-100 bg-white py-2 shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {options.map((option) => {
            const isSelected = option === value;
            return (
              <li key={option} role="option" aria-selected={isSelected} ref={isSelected ? selectedRef : undefined}>
                <button
                  type="button"
                  onClick={() => select(option)}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-[13px] font-medium transition-colors ${
                    isSelected ? 'bg-green-50 text-green-700' : 'text-[#18181b] hover:bg-green-50 hover:text-green-700'
                  }`}
                >
                  {option}
                  {isSelected && <FiCheck className="h-3.5 w-3.5 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
